import { MEAL_PLAN_TIME_ZONE_LOCALE } from "@/utils/constant";
import moment from "moment-timezone";

export const today = new Date(); // Stores today's date

/**
 * Generates an array of date objects centered around a given date.
 * - 3-week calendar view and starts from the Monday of the previous week.
 */
export const generateWeekDays = (baseDate: Date, daysOfWeek: any[]) => {
  const result = [];

  // Week view → start from nearest past Monday
  const dayOfWeek = baseDate.getDay();
  const startDate = new Date(baseDate);
  startDate.setDate(baseDate.getDate() - ((dayOfWeek + 6) % 7) - 7); // go back 1 week from Monday

  for (let i = 0; i < 21; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    result.push(dayDataObject(date, daysOfWeek));
  }
  return result;
};

export const generateMobileWeekDays = (baseDate: Date, daysOfWeek: any[]) => {
  const result = [];

  const dayOfWeek = baseDate.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
  const startDate = new Date(baseDate);

  // If not Monday (dayOfWeek !== 1), go back to previous Monday
  if (dayOfWeek !== 1) {
    startDate.setDate(baseDate.getDate() - ((dayOfWeek + 6) % 7));
  }

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    result.push(dayDataObject(date, daysOfWeek));
  }

  return result;
};

export const generateMobileWeekFromMonday = (
  mondayDate: Date,
  daysOfWeek: any[]
) => {
  const result = [];
  // Start from the day after mondayDate

  const selectedMonday = new Date(mondayDate);
  const startDate = new Date(selectedMonday);
  const dayOfWeek = selectedMonday.getDay();
  if (dayOfWeek !== 1) {
    startDate.setDate(selectedMonday.getDate() + 1);
  }
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    result.push(dayDataObject(date, daysOfWeek));
  }

  return result;
};

export const getNextMobileWeekData = (
  startFromDate: Date,
  daysOfWeek: any[]
) => {
  const result = [];

  const startDate = new Date(startFromDate);
  startDate.setDate(startFromDate.getDate() + 1); // Move to the next day (Monday next week)

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    result.push(dayDataObject(date, daysOfWeek));
  }

  return result;
};

export const getPreviousMobileWeekData = (
  startFromDate: Date,
  daysOfWeek: any[]
) => {
  const result = [];

  const startDate = new Date(startFromDate);
  startDate.setDate(startFromDate.getDate() - 7); // Go back exactly one week

  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    result.push(dayDataObject(date, daysOfWeek));
  }

  return result;
};

/**
 * Gets the last date of a given slide index.
 */
export const getDate = (slide: number) => {
  return (slide + 1) * 7 - 1;
};

/**
 * Finds the most recent Sunday before or on the given date.
 */
export const getPreviousSunday = (baseDate: Date): Date => {
  const newDate = new Date(baseDate); // Create a copy to avoid modifying the original date
  const dayOfWeek = newDate.getDay();

  // If it's already Sunday, move back 7 days; otherwise, move to the last Sunday
  const daysToSubtract = dayOfWeek === 0 ? 7 : dayOfWeek;
  newDate.setDate(newDate.getDate() - daysToSubtract);

  return newDate;
};

/**
 * Generates three consecutive weeks of dates starting from the previous Monday.
 */
export const generateThreeWeeks = (
  baseDate: Date,
  currentSlide: number,
  daysOfWeek: any[]
) => {
  const result = [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Convert baseDate to Monday of selected week
  const dayOfWeek = baseDate.getDay(); // Sunday = 0
  const selectedMonday = new Date(baseDate);
  let offsetWeeks;
  if (dayOfWeek !== 1) {
    selectedMonday.setDate(baseDate.getDate() - ((dayOfWeek + 6) % 7));
    offsetWeeks = currentSlide * -1 + 1;
  } else {
    offsetWeeks = -currentSlide;
  }

  // Need this part only if calendars stats from monday
  const startDate = new Date(selectedMonday);
  startDate.setDate(startDate.getDate() + offsetWeeks * 7);

  for (let i = 0; i < 21; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);

    const formattedDate = date.toLocaleDateString(MEAL_PLAN_TIME_ZONE_LOCALE);

    result.push({
      id: formattedDate,
      day: daysOfWeek[date.getDay()],
      date: date,
      formatedDate: formattedDate,
      isActive:
        formattedDate === today.toLocaleDateString(MEAL_PLAN_TIME_ZONE_LOCALE),
    });
  }

  return result;
};

/**
 * Generates date data for the three slides preceding the current view.
 * - Week view, slides start on Mondays.
 */
export const updatePreviousSlidesData = (
  currentStartDate: Date,
  itemsPerSlide: number,
  daysOfWeek: any[]
) => {
  const totalSlides = 3;

  let previousStartDates: Date[] = [];

  // Ensure previous start dates are Mondays
  const dayOfWeek = currentStartDate.getDay();
  const currentMonday = new Date(currentStartDate);
  currentMonday.setDate(currentStartDate.getDate() - ((dayOfWeek + 6) % 7));

  previousStartDates = Array.from({ length: totalSlides }, (_, i) => {
    const monday = new Date(currentMonday);
    monday.setDate(currentMonday.getDate() - (totalSlides - i) * 7);
    return monday;
  });

  const updatedData = previousStartDates
    .map((startDate) => {
      return Array.from({ length: itemsPerSlide }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        return dayDataObject(date, daysOfWeek);
      });
    })
    .flat();

  return updatedData;
};

/**
 * Generates date data for the three slides following the current view.
 * - Each slide contains itemsPerSlide days.
 * - Slides are spaced forward from the currentStartDate accordingly.
 */
export const updateNextSlidesData = (
  currentStartDate: Date,
  itemsPerSlide: number,
  daysOfWeek: any[]
) => {
  const totalSlides = 3;
  let nextStartDates: Date[] = [];

  nextStartDates = Array.from({ length: totalSlides }, (_, i) => {
    const start = new Date(currentStartDate);
    start.setDate(currentStartDate.getDate() + (i + 1) * itemsPerSlide);
    return start;
  });

  const updatedData = nextStartDates
    .map((startDate) => {
      return Array.from({ length: itemsPerSlide }, (_, i) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        return dayDataObject(date, daysOfWeek);
      });
    })
    .flat();

  return updatedData;
};

/**
 * Creates a data object for a given date.
 */
// const dayDataObject = (date: Date) => {
//   console.log(date,'date333')
//   return {
//     id: date.toISOString().split("T")[0],
//     day: daysOfWeek[date.getDay()],
//     date: date,
//     formatedDate: date.toISOString().split("T")[0],
//     isActive:
//       date.toISOString().split("T")[0] === today.toISOString().split("T")[0],
//   };
// };

const dayDataObject = (date: Date, daysOfWeek: any[]) => {
  const formatted = date.toLocaleDateString(MEAL_PLAN_TIME_ZONE_LOCALE); // YYYY-MM-DD in local time

  return {
    id: formatted,
    day: daysOfWeek[date.getDay()],
    date: date,
    formatedDate: formatted,
    isActive:
      formatted === today.toLocaleDateString(MEAL_PLAN_TIME_ZONE_LOCALE),
  };
};

export const getShortDay = (day: string): string => {
  return day.replace(/\s+/g, "").slice(0, 3);
};

export const getShortMonthDate = (date: string | number | Date): string => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return moment.tz(date, timeZone).format("MMM D, YYYY");
};

//Server side Dates
export function getStartAndEndDatesForMealPlanServer(timeZone: string) {
  const today = moment.tz(timeZone).startOf("day");
  // Start of the current ISO week (Monday) in user's timezone
  const currentWeekMonday = today.clone().startOf("isoWeek");
  // 4 weeks back from current Monday
  const startDate = currentWeekMonday.clone().subtract(4, "weeks");
  // 63 days from start (9 weeks)
  const endDate = startDate.clone().add(62, "days");
  return {
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
  };
}
export const isDateOutOfMealPlanRangeServer = (date: any, timeZone: string) => {
  const dateToCheck = moment.tz(date, timeZone).startOf("day");
  const { startDate, endDate } = getStartAndEndDatesForMealPlanServer(timeZone);
  const start = moment.tz(startDate, timeZone).startOf("day");
  const end = moment.tz(endDate, timeZone).startOf("day");
  return dateToCheck.isBefore(start) || dateToCheck.isAfter(end);
};

//Client side Dates
export const isDateOutOfMealPlanRange = (date: any) => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateToCheck = moment.tz(date, timeZone).startOf("day");
  const { startDate, endDate } = getStartAndEndDatesForMealPlan();
  const start = moment.tz(startDate, timeZone).startOf("day");
  const end = moment.tz(endDate, timeZone).startOf("day");
  return dateToCheck.isBefore(start) || dateToCheck.isAfter(end);
};

export function getStartAndEndDatesForMealPlan() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = moment.tz(timeZone).startOf("day");
  // Go to Monday of current week
  const currentWeekMonday = today.clone().startOf("isoWeek"); // ISO week starts on Monday
  // Go 4 weeks back
  const startDate = currentWeekMonday.clone().subtract(4, "weeks");
  // End date is 63 days after start (9 weeks total)
  const endDate = startDate.clone().add(62, "days");
  return {
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),
  };
}

export const startAutoScroll = (
  containerRef: any,
  scrollInterval: any,
  mouseYRef: any
) => {
  if (scrollInterval.current !== null) return;

  const step = () => {
    if (!containerRef?.current || !mouseYRef?.current) return;

    const pointerY = mouseYRef.current;
    const container = containerRef.current.find((el: any) => {
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return pointerY >= rect.top && pointerY <= rect.bottom;
    });

    if (!container) {
      scrollInterval.current = requestAnimationFrame(step);
      return;
    }

    const rect = container.getBoundingClientRect();
    const centerY = rect.top + rect.height / 2;
    const distanceFromCenter = pointerY - centerY;

    const threshold = 20;
    if (Math.abs(distanceFromCenter) > threshold) {
      const maxSpeed = 2;
      const baseSpeed = 0.4;
      const speed =
        Math.min(maxSpeed, Math.abs(distanceFromCenter) / 15) + baseSpeed;

      const scrollTopBefore = container.scrollTop;
      container.scrollTop += distanceFromCenter > 0 ? speed : -speed;
      const scrollTopAfter = container.scrollTop;

      if (scrollTopBefore === scrollTopAfter) {
        stopAutoScroll(scrollInterval);
        return;
      }
    }

    scrollInterval.current = requestAnimationFrame(step);
  };

  scrollInterval.current = requestAnimationFrame(step);
};

export const stopAutoScroll = (scrollInterval: any) => {
  if (scrollInterval.current !== null) {
    cancelAnimationFrame(scrollInterval.current);
    scrollInterval.current = null;
  }
};

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
  debounced.cancel = () => {
    if (timeout) clearTimeout(timeout);
  };
  return debounced;
};
